// @givmo/design-system — AppSwitcher, the waffle (EP-SUITE3; re-shaped at shell contract v3, D6;
// redesigned at OM-34 as direction C, "Two registers", RULED 2026-10-01 07:01 PDT).
//
// The nine-dot waffle in every Givmo suite portal's chrome, popping the person's APP LIST. Since
// shell contract v3 the popup IS the app list — every app the organization's class offers, in two
// sections that OM-34 gives two different registers:
//
//   "My Givmo Apps"   — the apps this person can OPEN, as a launcher of blue squares on white (may be
//                       a single square; the Admin Console square last, its count on the icon's
//                       corner). An EMPTY first section says so: "You can’t open any apps yet."
//   a subtle line;
//   the rest          — every other app the organization's class carries, as compact rows on a
//                       quiet sunken shelf: an outlined glyph, the name and exactly ONE status line
//                       with its door — locked · off · setting-up · requested · declined, and OM-33's
//                       "Coming soon". No visible heading: the line and the change of ground say "the
//                       rest", and the group keeps the name "More Givmo apps" for assistive tech.
//
// Blue is spent in one place — on what you can open. The current app is marked by its icon's
// stronger tint alone; the trigger reads as pressed (neutral grey, never blue) while the popup is
// open. The surface is the SaaS popup skin.
//
// LANDSCAPE from the tablet breakpoint — 528px wide, THREE per row in both registers — and two per row
// on a phone, where it takes the viewport's own gutter on both sides. NO footer (Jonathan, 2026-09-02:
// "I don't think there should be an All Apps link at the bottom"); the `footer` slot survives for the
// legacy single-grid shape below and is unused here.
//
// ⚠ NO TILE CARRIES AN OPTIONS MENU (RULED 2026-10-01 07:02 PDT, amending OM-14 T3/D6): the one item
// it held, "Set as default landing page", lives in each person's own preferences — the settings
// page's Landing card — on a phone and at desktop alike.
//
// ⚠ IT IS ALWAYS REAL, so the in-console waffle is UNGATED. The popup is the person's own list and
// never empty: someone with nothing openable sees the empty first section's sentence and the
// requestable apps below it — which is the point of the second section.
//
// ⚠ TWO SHAPES, ONE COMPONENT. A caller passing `mine`/`more` gets the two registers; a caller passing
// `modules` alone gets the pre-v3 single grid (300px, three per row from `sm`, the optional footer).
// No app passes it any more — the employer portal moved to the sections — so it retires with its
// design-system tests.
//
// ⚠ THE GLYPH IS THE ROUND DOTS (lucide `Grip`), not a squares grid — the Google-workspace convention
// the suite deliberately mirrors, ruled 2026-08-31 (OM-13).
//
// ⚠ AND ITS PLACE IS THE TOP-RIGHT UTILITY CLUSTER — [settings] · [waffle] · [avatar], each icon
// rendering only where its destination is real. `align` exists for that: anchored at the right edge
// of a 390px bar, a popup hanging from its START edge runs off the viewport.
//
// ⚠ IT IS NOT AN ORG SWITCHER, and the two must never be collapsed. The waffle answers "which of my
// apps do I want", the org switcher answers "which organization am I acting for".
//
// ARIA: this is a POPOVER, not a menu, and that is deliberate. Its content is lists of LINKS, and a
// `role="menu"` would demand `menuitem` children — which strips the link affordances a person
// reasonably expects from an app grid (middle-click, open in a new tab, copy the address). Base UI's
// `Popover.Trigger` therefore wires `aria-haspopup="dialog"` + `aria-expanded` + `aria-controls`, and
// the popup is a labelled `role="dialog"` holding the lists. Escape, outside-click dismissal, and
// focus restore to the trigger come from Base UI.
//
// ⚠ IT CLOSES ITSELF AFTER AN IN-APP HOP and when a row opens the request dialog; and a press inside
// a NESTED popup (a declined row's note popover — portalled outside this popup's DOM) is NOT an
// outside press.
//
// DATA CONTRACT: `app-switcher-trigger` (the button), `app-switcher-popover` (the popup),
// `app-switcher-mine` + `app-switcher-more` (the two lists — `-mine` stays in the DOM, hidden, when
// the first section is empty) + `app-switcher-divider` (the line) + `app-switcher-mine-empty` (the
// empty section's sentence), and the legacy `app-switcher-grid`; plus `module-square-<slug>` per
// square from ModuleGrid.
import { useCallback, useState, type ReactNode } from 'react';
import { Grip } from 'lucide-react';
import { Icon } from './icon';
import { IconButton } from './icon-button';
import { Popover } from './popover';
import { ModuleGrid, type ModuleNavigate, type ModuleSquareProps } from './module-grid';
import { cn } from '../lib/utils';

export interface AppSwitcherProps {
  /**
   * LEGACY single-grid shape: the squares this caller may open, in display order. Ignored when
   * `mine` is supplied. Retires with its tests — no app passes it.
   */
  modules?: readonly ModuleSquareProps[];
  /** v3: the apps the person can OPEN — the first section. May be empty. */
  mine?: readonly ModuleSquareProps[];
  /** v3: every other app the organization's class carries — the second section. May be empty. */
  more?: readonly ModuleSquareProps[];
  /** Heading of the first section (v3) or of the legacy grid. Defaults per shape. */
  heading?: string;
  /** Accessible name of the second section (v3; it has no visible heading). "More Givmo apps". */
  moreHeading?: string;
  /** Optional footer row — LEGACY shape only; the v3 popup renders none by ruling. */
  footer?: ReactNode;
  /** Accessible label for the trigger button. Defaults to "Switch Givmo app". */
  triggerLabel?: string;
  /**
   * In-app navigation for squares the host marked `inApp` — the same seam {@link ModuleGrid} takes.
   * The popup closes itself around it; see the header.
   */
  onNavigate?: ModuleNavigate;
  /**
   * Which of the trigger's edges the popup hangs from. `'start'` by default, which is what the
   * control had while it lived at the top-LEFT of a sidebar header.
   *
   * ⚠ PASS `'end'` FROM EVERY TOP-RIGHT PLACEMENT. The popup anchors to the trigger, so from a
   * trigger ~44px in from the right edge of a 390px viewport a `start` anchor would put most of it
   * off-screen.
   */
  align?: 'start' | 'center' | 'end';
  /** Extra classes for the trigger. */
  className?: string;
}

/** The v3 popup's own accessible name. */
const V3_POPUP_LABEL = 'Your Givmo apps';
const V3_MINE_HEADING = 'My Givmo Apps';
const V3_MORE_HEADING = 'More Givmo apps';
/**
 * OM-34 — an EMPTY first section says so, rather than leaving a heading over nothing (which read as
 * broken). Pinned copy.
 */
export const APP_SWITCHER_EMPTY_LINE = 'You can’t open any apps yet.';

/** Is this press inside a popup NESTED in ours (a declined row's note), portalled elsewhere? */
export function isNestedPopupPress(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const inNested = target.closest('[role="dialog"]') !== null;
  const inOurs = target.closest('[data-testid="app-switcher-popover"]') !== null;
  return inNested && !inOurs;
}

/** The first section's heading and the empty section's sentence share the rows' left edge. */
const SECTION_TEXT = 'px-8 pb-4 pt-4 font-sans tracking-givmo';

export function AppSwitcher({
  modules,
  mine,
  more,
  heading,
  moreHeading = V3_MORE_HEADING,
  footer,
  triggerLabel = 'Switch Givmo app',
  onNavigate,
  align = 'start',
  className,
}: AppSwitcherProps) {
  const [open, setOpen] = useState(false);
  const sections = mine !== undefined;

  const close = useCallback(() => setOpen(false), []);
  const navigate = useCallback<ModuleNavigate>(
    (href) => {
      setOpen(false);
      onNavigate?.(href);
    },
    [onNavigate],
  );

  /**
   * A row that opens the request dialog closes the popup behind it — a request dialog over a
   * still-open popup is two modal things at once. Wrapping the host's callbacks here keeps the
   * host's own squares oblivious to the popup they happen to be rendered in.
   */
  const closing = useCallback(
    (squares: readonly ModuleSquareProps[]): ModuleSquareProps[] =>
      squares.map((square) => {
        if (square.state === 'locked' || (square.state === 'off' && square.turnOn !== true)) {
          const onRequestAccess = square.onRequestAccess;
          return onRequestAccess === undefined
            ? square
            : {
                ...square,
                onRequestAccess: () => {
                  close();
                  onRequestAccess();
                },
              };
        }
        return square;
      }),
    [close],
  );

  const trigger = (
    <Popover.Trigger
      render={
        <IconButton
          aria-label={triggerLabel}
          tone="ghost"
          className={cn(
            className,
            // The trigger reads as PRESSED while its popup is open — the convention the waffle
            // mirrors. Neutral, never blue: blue is for action and the apps you can open.
            sections && 'data-[popup-open]:bg-neutral-100 data-[popup-open]:text-text-strong',
          )}
          testId="app-switcher-trigger"
        >
          <Icon icon={Grip} size={18} aria-hidden />
        </IconButton>
      }
    />
  );

  if (!sections) {
    const legacyHeading = heading ?? V3_POPUP_LABEL;
    return (
      <Popover.Root open={open} onOpenChange={setOpen}>
        {trigger}
        <Popover.Content
          side="bottom"
          align={align}
          aria-label={legacyHeading}
          // ⚠ ANCHORED AT DESKTOP, INSET AT PHONE. Below `sm` the popup takes the viewport's own
          // gutter on both sides (12px), so it reads as placed rather than as having stopped
          // wherever it first fit.
          collisionPadding={12}
          className="w-[300px] p-12 max-sm:w-[calc(100vw-24px)]"
          testId="app-switcher-popover"
        >
          <p className="mb-8 px-4 font-sans text-s11 font-w600 uppercase tracking-wide text-text-tertiary">
            {legacyHeading}
          </p>
          <ModuleGrid
            modules={modules ?? []}
            columns={3}
            onNavigate={onNavigate === undefined ? undefined : navigate}
            aria-label={legacyHeading}
            testId="app-switcher-grid"
          />
          {footer !== undefined && (
            <div className="mt-8 border-t border-border-subtle pt-8">{footer}</div>
          )}
        </Popover.Content>
      </Popover.Root>
    );
  }

  const mineHeading = heading ?? V3_MINE_HEADING;
  const mineSquares = closing(mine);
  const moreSquares = closing(more ?? []);
  const empty = mineSquares.length === 0;

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next, details) => {
        // A press inside a nested popup is not an outside press — see the header.
        if (
          !next &&
          isNestedPopupPress((details as { event?: Event } | undefined)?.event?.target ?? null)
        ) {
          return;
        }
        setOpen(next);
      }}
    >
      {trigger}
      <Popover.Content
        saas
        side="bottom"
        align={align}
        sideOffset={6}
        aria-label={V3_POPUP_LABEL}
        collisionPadding={12}
        // LANDSCAPE: 528px from the tablet breakpoint (three per row in both registers); the
        // viewport's gutter on a phone (two per row). Scrolls within the viewport for a long list.
        // The SaaS skin, opened up: no inner padding (each register spaces itself), a 16px radius
        // and the large soft shadow of a surface that floats over the page.
        className="max-h-[calc(100vh-72px)] w-[528px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-2xl p-0 shadow-xl max-md:w-[calc(100vw-24px)]"
        testId="app-switcher-popover"
      >
        <div className="p-12">
          <p className={cn(SECTION_TEXT, 'text-s12 font-w600 text-text-secondary')}>
            {mineHeading}
          </p>
          <ModuleGrid
            modules={mineSquares}
            density="launcher"
            onNavigate={navigate}
            aria-label={mineHeading}
            testId="app-switcher-mine"
            hidden={empty}
          />
          {empty && (
            <p
              className={cn(SECTION_TEXT, 'text-s13 text-text-tertiary')}
              data-testid="app-switcher-mine-empty"
            >
              {APP_SWITCHER_EMPTY_LINE}
            </p>
          )}
        </div>
        {moreSquares.length > 0 && (
          <>
            {/* The subtle line Jonathan named (D6) — the shelf's top edge. */}
            <div
              role="separator"
              aria-orientation="horizontal"
              className="border-t border-border-subtle"
              data-testid="app-switcher-divider"
            />
            <div className="bg-surface-sunken p-12">
              <ModuleGrid
                modules={moreSquares}
                density="shelf"
                onNavigate={navigate}
                aria-label={moreHeading}
                testId="app-switcher-more"
              />
            </div>
          </>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}
